import React from 'react';
import { Path, Svg } from 'react-native-svg';
import type { SvgProps } from 'react-native-svg';
import { useUnistyles } from 'react-native-unistyles';

import type { AppColors } from '@squire/ui';

const VIEWBOX_WIDTH = 47;
const VIEWBOX_HEIGHT = 15;

interface SquireLogoTextProps extends SvgProps {
  size?: number;
  /**
   * Semantic token for the wordmark fill. Omit to keep the static white
   * wordmark, which only reads on a dark surface.
   */
  color?: keyof AppColors;
}

export const SquireLogoText: React.FC<SquireLogoTextProps> = ({
  size = 47,
  color,
  ...props
}) => {
  const { theme: colors } = useUnistyles();
  const width = size;
  const height = (size * VIEWBOX_HEIGHT) / VIEWBOX_WIDTH;
  const fill = color ? colors[color] : 'white';

  return (
    <Svg
      {...props}
      width={width}
      height={height}
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
      fill="none"
    >
      <Path
        d="M0.0606814 3.33955C0.0606814 2.26431 0.337119 1.44208 0.889994 0.872835C1.44287 0.290945 2.2587 0 3.33748 0C4.41626 0 5.23209 0.290945 5.78496 0.872835C6.33784 1.44208 6.61428 2.26431 6.61428 3.33955V3.75699H4.51065V3.20672C4.51065 2.75133 4.41626 2.42244 4.22747 2.22004C4.03869 2.00499 3.76225 1.89747 3.39816 1.89747C3.03407 1.89747 2.75763 2.00499 2.56885 2.22004C2.38006 2.42244 2.28567 2.75133 2.28567 3.20672C2.28567 3.76332 2.44074 4.25033 2.75089 4.66778C3.07453 5.08522 3.55323 5.55959 4.18702 6.09088C4.71292 6.54627 5.13769 6.95107 5.46133 7.30526C5.78496 7.6468 6.0614 8.06425 6.29064 8.55759C6.51988 9.05093 6.6345 9.61385 6.6345 10.2463C6.6345 11.3216 6.35132 12.1501 5.78496 12.732C5.2186 13.3013 4.39603 13.5859 3.31725 13.5859C2.23847 13.5859 1.4159 13.3013 0.84954 12.732C0.28318 12.1501 0 11.3216 0 10.2463V9.43043H2.10362V10.3792C2.10362 11.2393 2.48794 11.6694 3.25657 11.6694C4.0252 11.6694 4.40952 11.2393 4.40952 10.3792C4.40952 9.82257 4.2477 9.33555 3.92407 8.91811C3.61392 8.50067 3.14195 8.0263 2.50817 7.49501C1.98226 7.03961 1.55749 6.64115 1.23386 6.2996C0.910222 5.94541 0.633784 5.52164 0.404543 5.0283C0.175302 4.53495 0.0606814 3.97204 0.0606814 3.33955Z"
        fill={fill}
      />
      <Path
        d="M8.61793 3.33955C8.61793 2.27696 8.90786 1.45473 9.4877 0.872835C10.0675 0.290945 10.9036 0 11.9959 0C13.0881 0 13.9242 0.290945 14.504 0.872835C15.0839 1.45473 15.3738 2.27696 15.3738 3.33955V10.2463C15.3738 10.9927 15.2322 11.6125 14.949 12.1059C15.003 12.2197 15.0704 12.2956 15.1513 12.3336C15.2457 12.3715 15.3873 12.3905 15.5761 12.3905H15.7783V14.25H15.4749C14.5175 14.25 13.877 13.9464 13.5534 13.3392C13.0679 13.5037 12.5487 13.5859 11.9959 13.5859C10.9036 13.5859 10.0675 13.2949 9.4877 12.713C8.90786 12.1312 8.61793 11.3089 8.61793 10.2463V3.33955ZM10.8429 10.3792C10.8429 11.252 11.2272 11.6884 11.9959 11.6884C12.7645 11.6884 13.1488 11.252 13.1488 10.3792V3.20672C13.1488 2.33389 12.7645 1.89747 11.9959 1.89747C11.2272 1.89747 10.8429 2.33389 10.8429 3.20672V10.3792Z"
        fill={fill}
      />
      <Path
        d="M19.8343 10.3981C19.8343 11.2583 20.2186 11.6884 20.9872 11.6884C21.7558 11.6884 22.1402 11.2583 22.1402 10.3981V0.151797H24.2438V10.2653C24.2438 11.3405 23.9606 12.1691 23.3942 12.751C22.8279 13.3202 22.0053 13.6049 20.9265 13.6049C19.8477 13.6049 19.0252 13.3202 18.4588 12.751C17.8925 12.1691 17.6093 11.3405 17.6093 10.2653V0.151797H19.8343V10.3981Z"
        fill={fill}
      />
      <Path
        d="M28.8853 0.151797V13.4341H26.6604V0.151797H28.8853Z"
        fill={fill}
      />
      <Path
        d="M36.0551 13.4341C35.9742 13.219 35.9203 13.0166 35.8933 12.8269C35.8664 12.6372 35.8529 12.2956 35.8529 11.8023V9.71505C35.8529 9.10786 35.7383 8.67776 35.509 8.42477C35.2798 8.15912 34.9089 8.0263 34.3965 8.0263H33.6279V13.4341H31.4029V0.151797H34.7606C35.9068 0.151797 36.7429 0.404793 37.2688 0.910785C37.7947 1.40413 38.0576 2.16944 38.0576 3.20672V4.25033C38.0576 5.61651 37.5722 6.51465 36.6013 6.94474C37.1272 7.14714 37.5048 7.48868 37.734 7.96937C37.9632 8.43742 38.0779 9.03828 38.0779 9.77197V11.8212C38.0779 12.2007 38.0913 12.5107 38.1183 12.751C38.1453 12.9787 38.2127 13.2064 38.3206 13.4341H36.0551ZM33.6279 6.12883H34.4976C34.9426 6.12883 35.273 6.02131 35.4888 5.80626C35.718 5.59121 35.8326 5.23069 35.8326 4.7247V3.41545C35.8326 2.93475 35.7382 2.58688 35.5495 2.37184C35.3742 2.15679 35.091 2.04927 34.6999 2.04927H33.6279V6.12883Z"
        fill={fill}
      />
      <Path
        d="M45.7111 5.74933V7.6468H42.6568V11.5366H46.5V13.4341H40.4319V0.151797H46.5V2.04927H42.6568V5.74933H45.7111Z"
        fill={fill}
      />
    </Svg>
  );
};
